import { useCallback } from 'react';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { X } from '@carboledger/ui/icons';
// TODO: import File from @carboledger/ui/icons once it re-exports it.
import { File as FileIcon } from 'lucide-react';
import { cx } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import styles from './FileAttachmentCard.module.scss';

/**
 * Props:
 *   file         the File to show
 *   onView       (file) => void
 *   onRemove     (file) => void
 *   iconSize     px size of the leading file icon
 *   removeLabel  renders Remove as a text button with this label; omit for an icon-only X
 *   bordered     draws the card border
 *   className    applied to the row, for callers needing different spacing
 */
export const FileAttachmentCard = ({
  file,
  onView,
  onRemove,
  iconSize = 24,
  removeLabel,
  bordered = true,
  className,
}) => {
  const t = useTranslations();
  const handleView = useCallback(() => onView(file), [onView, file]);
  const handleRemove = useCallback(() => onRemove(file), [onRemove, file]);

  return (
    <div className={cx(styles.card, bordered && styles.bordered, className)}>
      <FileIcon size={iconSize} className={styles.icon} aria-hidden="true" />
      <div className={styles.name}>
        <Typography
          variant={TypographyVariants.bodyXs}
          weight={TypographyWeights.semibold}
          color={TypographyColors.inherit}
          truncate
        >
          {file.name}
        </Typography>
      </div>
      <Button
        variant={ButtonVariants.tertiary}
        size={ButtonSizes.sm}
        className={styles.action}
        onClick={handleView}
      >
        {t('COMMON_VIEW')}
      </Button>
      {removeLabel ? (
        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          className={styles.action}
          onClick={handleRemove}
        >
          {removeLabel}
        </Button>
      ) : (
        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          iconOnly
          className={styles.remove}
          aria-label={t('FORM_REMOVE_FILE', { name: file.name })}
          onClick={handleRemove}
        >
          <X size={20} />
        </Button>
      )}
    </div>
  );
};
